import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { AccessibilityProvider } from './components/accessibility';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import CommandPalette from './components/CommandPalette';
import { SkipLinks } from './components/accessibility';
import KeyboardShortcuts from './components/accessibility/KeyboardShortcuts';
import './styles/responsive.css';
import { protectedRoutes, publicRoutes, renderRoutes } from './routes';

/**
 * URGAA Dashboard Application
 * GO4GARAGE URGAA - Charging Tomorrow with Green Energy
 * 
 * Features:
 * - WCAG 2.1 AA Compliant
 * - Mobile-first responsive design
 * - Dark/Light theme support
 * - Keyboard navigation
 * - Screen reader support
 */

// Loading fallback with skeleton - defined outside App to prevent re-creation
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-urga-electricBlue mx-auto" />
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xl">⚡</span>
      </div>
      <p className="text-gray-600 dark:text-gray-400 mt-4">Loading URGAA...</p>
    </div>
  </div>
);

function App() {
  // Runtime cleanup: Remove any injected badges
  useEffect(() => {
    const cleanup = () => {
      const selectors = [
        '#emergent-badge',
        '.emergent-badge',
        '[data-emergent-badge]',
        'a[href*="emergent.sh"][href*="utm_source"]',
        'a[aria-label*="Emergent"]'
      ];
      
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(node => {
          node.remove();
        });
      });
    };
    
    cleanup();
    const timer = setTimeout(cleanup, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AccessibilityProvider>
          <Router>
            <AuthProvider>
              <ActivityProvider>
                <SkipLinks />
                <CommandPalette />
                <KeyboardShortcuts />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {renderRoutes(publicRoutes)}
                    <Route
                      path="/*"
                      element={(
                        <ProtectedRoute>
                          <Layout>
                            <Routes>{renderRoutes(protectedRoutes)}</Routes>
                          </Layout>
                        </ProtectedRoute>
                      )}
                    />
                  </Routes>
                </Suspense>
               </ActivityProvider>
          </AuthProvider>
        </Router>
      </AccessibilityProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
